import { useEffect, useRef, useState } from 'react';
import { formatDate } from '../dates.ts';
import { readTextPreview, TEXT_TYPE, type Viewer } from '../textPreview.ts';
import { TextPreview } from './TextPreview.ts';

/** What the preview needs to know about a document, whichever kind it is (a Clio copy or an upload). */
export interface PreviewDoc {
  id: string;
  name: string;
  date: string | null;
  /** How the preview tries to show it, from its name; the type the server served it as has the last word. */
  viewer: Viewer;
  /** A word for what it is ("Word", "Excel", ...), for the panel. */
  kind: string;
  bytes: number | null;
}

/** The types the server serves inline (api/src/upload-kind.ts, INLINE_KINDS). Nothing else is put in a frame, an
 *  image or the text view: a page or an SVG is served as an attachment, and the panel offers Download instead. */
const PDF = 'application/pdf';
const PICTURES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
function shows(viewer: PreviewDoc['viewer'], served: string): boolean {
  if (viewer === 'pdf') return served === PDF;
  if (viewer === 'image') return PICTURES.includes(served);
  if (viewer === 'text') return served === TEXT_TYPE;
  // A video plays in the player whatever it was served as: a <video> runs no script.
  return viewer === 'video';
}

/**
 * The preview beside the list. A PDF and a picture are shown in full and a video in the player, each fetched through
 * the same audited open as any document and let go when the preview moves on or closes. A video the browser cannot
 * decode (an iPhone .MOV in HEVC, often) says so and offers Download: never a broken frame. A .txt served as plain
 * text is shown as its text, the first 1 MB of a longer one (21 October). Any other type says what
 * it is and offers Download, and fetches nothing. Previous and next move through the files of the folder (or the
 * search) the reader is in; the arrow keys do too, and Escape closes.
 */
export function DocumentPreview({ doc, fetchBlob, position, onStep, onClose, onDownload, downloading, failed }: {
  doc: PreviewDoc; fetchBlob: () => Promise<{ blob: Blob }>; position: { at: number; of: number } | null;
  onStep: (step: number) => void; onClose: () => void; onDownload: () => void; downloading: boolean; failed: string | null;
}) {
  const [shown, setShown] = useState<{
    id: string; url: string; type: string; text?: { text: string; cut: boolean; bytes: number } | null;
  } | null>(null);
  const [error, setError] = useState<{ id: string; message: string } | null>(null);
  const [unplayable, setUnplayable] = useState<string | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => { heading.current?.focus({ preventScroll: true }); }, [doc.id]);

  useEffect(() => {
    if (doc.viewer === null) return;
    let live = true, url: string | null = null;
    setError(null);
    fetchBlob()
      .then(async ({ blob }) => {
        const type = blob.type.toLowerCase().split(';')[0]!.trim();
        if (doc.viewer === 'text') {
          // Read as text only when served as text; never an address the page could navigate to.
          const text = type === TEXT_TYPE ? await readTextPreview(blob) : null;
          if (live) setShown({ id: doc.id, url: '', type, text });
          return;
        }
        url = URL.createObjectURL(blob);
        if (live) setShown({ id: doc.id, url, type }); else URL.revokeObjectURL(url);
      })
      .catch((e: Error) => { if (live) setError({ id: doc.id, message: e.message }); });
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchBlob is a new function each render; the document is the key
  }, [doc.id, doc.viewer]);

  const ext = /\.([^.]+)$/.exec(doc.name)?.[1]?.toLowerCase() ?? null;
  const many = (position?.of ?? 0) > 1;

  return (
    <section id="document-preview" aria-labelledby="document-preview-title"
             onKeyDown={(e) => {
               if ((e.target as HTMLElement).closest('iframe, video')) return;
               if (e.key === 'Escape') { e.preventDefault(); onClose(); }
               else if (e.key === 'ArrowRight') { e.preventDefault(); onStep(1); }
               else if (e.key === 'ArrowLeft') { e.preventDefault(); onStep(-1); }
             }}
             className="min-w-0 rounded-lg border border-line bg-panel lg:sticky lg:top-4 lg:self-start">
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
        <h3 id="document-preview-title" ref={heading} tabIndex={-1}
            className="min-w-0 flex-1 break-all text-base font-semibold text-neutral-900 outline-none">
          {doc.name}
          <span className="ml-2 text-sm font-normal text-neutral-600">
            {position ? `${position.at + 1} of ${position.of}` : ''}{position && doc.date ? ', ' : ''}{doc.date ? formatDate(doc.date) : ''}
          </span>
        </h3>
        <button type="button" onClick={() => onStep(-1)} disabled={!many} className={button}>‹ Previous</button>
        <button type="button" onClick={() => onStep(1)} disabled={!many} className={button}>Next ›</button>
        <button type="button" onClick={onDownload} aria-disabled={downloading} className={button}>{downloading ? 'Saving…' : 'Download'}</button>
        <button type="button" onClick={onClose} className={button}>Close</button>
      </div>
      {failed && <p role="alert" className="px-3 pt-2 text-sm text-warning-fg">{failed}</p>}
      <div className="flex min-h-[50vh] items-center justify-center bg-neutral-100 p-2">
        {doc.viewer === null || (shown?.id === doc.id && !shows(doc.viewer, shown.type)) ? (
          <div className="max-w-md space-y-3 p-4 text-neutral-900">
            <p className="text-lg font-semibold">{describeKind(doc.kind, ext)}</p>
            <p className="text-neutral-800">
              {doc.bytes !== null ? `${size(doc.bytes)}. ` : ''}It can’t be shown here. Download it to open it on this computer.
            </p>
            <button type="button" onClick={onDownload} aria-disabled={downloading}
                    className="min-h-[44px] rounded-lg bg-brand px-4 font-semibold text-on-brand">
              {downloading ? 'Saving…' : 'Download'}
            </button>
          </div>
        ) : error?.id === doc.id ? (
          <p role="alert" className="text-warning-fg">{error.message}</p>
        ) : shown?.id === doc.id ? (
          doc.viewer === 'pdf' ? (
            // The viewer's own toolbar is hidden: its save would name the file after the blob: address. Download is above.
            <iframe key={shown.url} src={`${shown.url}#toolbar=0&navpanes=0`} title={doc.name}
                    className="h-[80dvh] min-h-[480px] w-full rounded border-0 bg-white" />
          ) : doc.viewer === 'text' ? (
            shown.text ? <TextPreview text={shown.text.text} cut={shown.text.cut} bytes={shown.text.bytes} /> : null
          ) : doc.viewer === 'video' ? (
            unplayable === doc.id ? (
              <p role="status" className="text-neutral-800">
                This video can’t play in the browser (some phones record in a format it can’t read). Download it to watch.
              </p>
            ) : (
              // eslint-disable-next-line jsx-a11y/media-has-caption -- a visit's own footage has no captions to offer
              <video key={shown.url} src={shown.url} controls preload="metadata" onError={() => setUnplayable(doc.id)}
                     className="max-h-[75vh] max-w-full" aria-label={doc.name} />
            )
          ) : (
            <img src={shown.url} alt={doc.name} className="max-h-[75vh] max-w-full object-contain" />
          )
        ) : (
          <p className="text-neutral-700">Loading…</p>
        )}
      </div>
    </section>
  );
}

/** "A Word document (.docx)": what a file is, in words, from the kind the API gave and its extension. */
function describeKind(kind: string, ext: string | null): string {
  const what: Record<string, string> = {
    Word: 'A Word document', Excel: 'An Excel spreadsheet', Email: 'An email', Audio: 'A sound recording',
    Text: 'A text file', Zip: 'A zip archive', Photo: 'A photo', Video: 'A video', PDF: 'A PDF', Shortcut: 'A shortcut',
  };
  const words = what[kind] ?? 'A file';
  return ext ? `${words} (.${ext})` : words;
}


const button = 'min-h-[44px] rounded-lg border border-neutral-400 px-3 font-semibold text-neutral-900 disabled:text-neutral-500';
const size = (n: number) => (n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);
